import React from 'react';
import { Box, Button, Typography } from '@p81-common/ui';
import { TenantValidation } from '../../../helpers/types';
import {
  ErrorAttentionBox,
  RuleViolationList,
  WarningAttentionBox
} from '../ui';
import { createClasses } from './styles';

export interface ValidationResultLabels {
  /** Heading - differs between "some were skipped" and "none could start". */
  title: string;
  body: string;
  batchId: string;
  willMigrateCount: string;
  skippedCount: string;
  willMigrateTitle: string;
  skippedTitle: string;
  actionLabel: string;
}

interface ValidationResultScreenBaseProps {
  labels: ValidationResultLabels;
  tenants: TenantValidation[];
  validCount: number;
  invalidCount: number;
  /** Correlates this attempt with its Datadog lines and with support. */
  batchId?: string;
}

// The CTA must do something - callers provide exactly one of the two actions,
// the same contract CompletionScreen enforces. With both optional it is possible
// to render a button that silently does nothing.
type ValidationResultScreenProps = ValidationResultScreenBaseProps &
  (
    | {
        /** Opened in a new tab: the batch's Datadog log stream. */
        actionHref: string;
        onAction?: never;
      }
    | {
        /** Called on click when nothing started, so the CTA can close. */
        onAction: () => void;
        actionHref?: never;
      }
  );

// Shown whenever pre-flow validation skipped at least one tenant - both the
// partial 202 and the all-invalid 400. Without it a 1-of-20 batch looks exactly
// like a 20-of-20 batch, which is how skipped tenants used to go unnoticed.
const ValidationResultScreen = ({
  labels,
  tenants,
  validCount,
  invalidCount,
  batchId,
  actionHref,
  onAction
}: ValidationResultScreenProps) => {
  const classes = createClasses();

  const skipped = tenants.filter(({ status }) => status === 'invalid');
  const migrating = tenants.filter(({ status }) => status === 'valid');

  const handleAction = () => {
    if (actionHref) {
      window.open(actionHref, '_blank', 'noopener,noreferrer');
      return;
    }
    onAction?.();
  };

  // Nothing started at all: that is a failure, not a caveat on a running batch,
  // so it gets the error scheme. A partial batch did start, so it stays a warning.
  const nothingStarted = validCount === 0;
  const HeaderBox = nothingStarted ? ErrorAttentionBox : WarningAttentionBox;

  return (
    // The screen replaces the wizard after an async submit, so it has to
    // announce itself: assertively when nothing started, politely otherwise.
    <Box
      className={classes.container}
      role={nothingStarted ? 'alert' : 'status'}
      aria-live={nothingStarted ? 'assertive' : 'polite'}
    >
      <HeaderBox title={labels.title} subtitle={labels.body} />

      {/* The counts and the lists are one group: they are all the breakdown of
          the same attempt, so they sit closer to each other than to the header
          box above or the CTA below. */}
      <Box className={classes.results}>
        <Box className={classes.counts}>
          {/* A "0 being associated" tile is noise when the header already says
              nothing started - only the skipped count carries information. */}
          {/* Each pair is one figure: without the label bound to it, a screen
              reader announces a bare digit. */}
          {!nothingStarted && (
            <Box
              className={classes.count}
              aria-label={`${validCount} ${labels.willMigrateCount}`}
            >
              <Typography
                variant="body1"
                className={classes.countValue}
                aria-hidden
              >
                {validCount}
              </Typography>
              <Typography className={classes.countLabel} aria-hidden>
                {labels.willMigrateCount}
              </Typography>
            </Box>
          )}
          <Box
            className={classes.count}
            aria-label={`${invalidCount} ${labels.skippedCount}`}
          >
            <Typography
              variant="body1"
              className={classes.skippedCountValue}
              aria-hidden
            >
              {invalidCount}
            </Typography>
            <Typography className={classes.countLabel} aria-hidden>
              {labels.skippedCount}
            </Typography>
          </Box>
        </Box>

        {/* The only handle on this attempt once the drawer closes: when nothing
            started there is no View Logs button to carry it. */}
        {batchId && (
          <Box className={classes.batchId}>
            <Typography className={classes.countLabel}>
              {labels.batchId}
            </Typography>
            <Typography variant="body2" className={classes.batchIdValue}>
              {batchId}
            </Typography>
          </Box>
        )}

        {skipped.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="body2" className={classes.sectionTitle}>
              {labels.skippedTitle}
            </Typography>
            <Box component="ul" className={classes.tenantList}>
              {skipped.map(({ tenantId, validationErrors }) => (
                <Box
                  component="li"
                  key={tenantId}
                  className={classes.skippedTenant}
                >
                  <Typography variant="body2" className={classes.tenantId}>
                    {tenantId}
                  </Typography>
                  <RuleViolationList violations={validationErrors ?? []} />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {migrating.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="body2" className={classes.sectionTitle}>
              {labels.willMigrateTitle}
            </Typography>
            <Box component="ul" className={classes.tenantList}>
              {migrating.map(({ tenantId }) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={tenantId}
                  className={classes.migratingTenant}
                >
                  {tenantId}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Button
        variant="contained"
        color="primary"
        className={classes.actionButton}
        onClick={handleAction}
      >
        {labels.actionLabel}
      </Button>
    </Box>
  );
};

export default ValidationResultScreen;
